import React, { useState, useEffect } from 'react';
import { 
  BookOpen, 
  PlayCircle, 
  FileText, 
  Headphones, 
  Video, 
  Award, 
  Lock, 
  CheckCircle, 
  Star,
  Crown,
  Filter,
  Search,
  TrendingUp,
  Download,
  Users,
  Trophy,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const BootCampTrainingVault = () => {
  const { user } = useAuth();
  const [trainingContent, setTrainingContent] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [ebookProgress, setEbookProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedContentType, setSelectedContentType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('ebooks');

  // Content type configurations
  const contentTypeConfig = {
    ebook: { 
      icon: BookOpen, 
      color: 'blue', 
      label: 'eBook',
      bgClass: 'bg-blue-50 border-blue-200',
      textClass: 'text-blue-800',
      iconClass: 'text-blue-600'
    },
    podcast: { 
      icon: Headphones, 
      color: 'purple', 
      label: 'Podcast',
      bgClass: 'bg-purple-50 border-purple-200',
      textClass: 'text-purple-800',
      iconClass: 'text-purple-600'
    },
    video_course: { 
      icon: Video, 
      color: 'red', 
      label: 'Video Course',
      bgClass: 'bg-red-50 border-red-200',
      textClass: 'text-red-800',
      iconClass: 'text-red-600'
    },
    ad_copy: { 
      icon: FileText, 
      color: 'green', 
      label: 'Ad Copy',
      bgClass: 'bg-green-50 border-green-200',
      textClass: 'text-green-800',
      iconClass: 'text-green-600'
    },
    playbook: { 
      icon: BookOpen, 
      color: 'yellow', 
      label: 'Playbook',
      bgClass: 'bg-yellow-50 border-yellow-200',
      textClass: 'text-yellow-800',
      iconClass: 'text-yellow-600'
    },
    training_module: { 
      icon: PlayCircle, 
      color: 'indigo', 
      label: 'Training',
      bgClass: 'bg-indigo-50 border-indigo-200',
      textClass: 'text-indigo-800',
      iconClass: 'text-indigo-600'
    }
  };

  // Access level configurations
  const accessLevelConfig = {
    free: { label: 'Free', color: '#6B7280', icon: Users },
    boot_camp: { label: 'Boot Camp', color: '#F59E0B', icon: Award },
    rookie_pro: { label: 'Rookie Pro', color: '#10B981', icon: Star },
    pro: { label: 'Pro', color: '#3B82F6', icon: TrendingUp },
    enterprise: { label: 'Enterprise', color: '#8B5CF6', icon: Crown }
  };

  const fetchTrainingContent = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL;
      const token = localStorage.getItem('auth_token');
      
      const params = new URLSearchParams();
      if (selectedContentType !== 'all') params.append('content_type', selectedContentType);
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      
      const response = await fetch(`${backendUrl}/api/member/training-vault?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTrainingContent(data);
      } else {
        toast.error('Failed to load training content');
      }
    } catch (error) {
      console.error('Error fetching training content:', error);
      toast.error('Error loading training content');
    }
  };

  const fetchAchievements = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL;
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${backendUrl}/api/member/achievements`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAchievements(data);
      }
    } catch (error) {
      console.error('Error fetching achievements:', error);
    }
  };

  const fetchEbookProgress = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL;
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${backendUrl}/api/member/ebook-progress`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEbookProgress(data);
      }
    } catch (error) {
      console.error('Error fetching ebook progress:', error);
    }
  };

  const updateProgress = async (contentId, progress) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL;
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${backendUrl}/api/member/training-vault/${contentId}/progress`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ progress_percentage: progress })
      });
      
      if (response.ok) {
        const result = await response.json();
        
        if (result.achievements_unlocked > 0) {
          toast.success(result.message);
          fetchAchievements(); // Refresh achievements
        } else {
          toast.success('Progress updated!');
        }
        
        fetchTrainingContent(); // Refresh content to update progress
      }
    } catch (error) {
      console.error('Error updating progress:', error);
      toast.error('Failed to update progress');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTrainingContent(), fetchAchievements(), fetchEbookProgress()]);
      setLoading(false);
    };
    loadData();
  }, [selectedCategory, selectedContentType]);

  // Filter content based on search
  const filteredContent = trainingContent.filter(content =>
    content.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    content.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    content.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get unique categories
  const categories = ['all', ...new Set(trainingContent.map(content => content.category))];

  const renderContentCard = (content) => {
    const typeConfig = contentTypeConfig[content.content_type] || contentTypeConfig.training_module;
    const accessConfig = accessLevelConfig[content.access_level] || accessLevelConfig.free;
    const IconComponent = typeConfig.icon;

    return (
      <Card key={content.id} className={`hover:shadow-lg transition-shadow duration-200 ${typeConfig.bgClass}`}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg bg-white/80 ${typeConfig.iconClass}`}>
                <IconComponent className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className={`text-lg ${typeConfig.textClass}`}>{content.title}</CardTitle>
                <CardDescription className="text-sm mt-1">
                  {content.description}
                </CardDescription>
              </div>
            </div>
            <div className="flex flex-col items-end space-y-2">
              <Badge 
                style={{ backgroundColor: accessConfig.color, color: 'white' }}
                className="text-xs font-medium"
              >
                {accessConfig.label}
              </Badge>
              {content.featured && (
                <Badge variant="secondary" className="text-xs">
                  <Star className="h-3 w-3 mr-1" />
                  Featured
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="pt-0">
          <div className="space-y-3">
            {/* Content metadata */}
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span className="font-medium">{content.category}</span>
              <div className="flex items-center space-x-4">
                {content.duration_minutes && (
                  <span className="flex items-center">
                    <PlayCircle className="h-4 w-4 mr-1" />
                    {content.duration_minutes} min
                  </span>
                )}
                {content.page_count && (
                  <span className="flex items-center">
                    <FileText className="h-4 w-4 mr-1" />
                    {content.page_count} pages
                  </span>
                )}
              </div>
            </div>
            
            {/* Progress bar */}
            {content.user_progress > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Progress</span>
                  <span className="font-medium">{content.user_progress}%</span>
                </div>
                <Progress value={content.user_progress} className="h-2" />
              </div>
            )}
            
            {/* Tags */}
            {content.tags && content.tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {content.tags.slice(0, 3).map((tag, index) => (
                  <Badge key={index} variant="outline" className="text-xs px-2 py-1">
                    {tag}
                  </Badge>
                ))}
                {content.tags.length > 3 && (
                  <Badge variant="outline" className="text-xs px-2 py-1">
                    +{content.tags.length - 3} more
                  </Badge>
                )}
              </div>
            )}
            
            {/* Action buttons */}
            <div className="flex items-center justify-between pt-2">
              <div className="flex space-x-2">
                {content.has_access ? (
                  <>
                    <Button 
                      size="sm" 
                      className="bg-white/80 hover:bg-white text-gray-800 border"
                      onClick={() => updateProgress(content.id, content.user_progress === 100 ? 0 : 100)}
                    >
                      {content.user_progress === 100 ? (
                        <>
                          <CheckCircle className="h-4 w-4 mr-1 text-green-600" />
                          Completed
                        </>
                      ) : (
                        <>
                          <PlayCircle className="h-4 w-4 mr-1" />
                          {content.user_progress > 0 ? 'Continue' : 'Start'}
                        </>
                      )}
                    </Button>
                    {content.user_progress > 0 && content.user_progress < 100 && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="bg-white/60"
                        onClick={() => updateProgress(content.id, 100)}
                      >
                        Mark Complete
                      </Button>
                    )}
                  </>
                ) : (
                  <Button size="sm" variant="outline" disabled>
                    <Lock className="h-4 w-4 mr-1" />
                    Locked
                  </Button>
                )}
              </div>
              
              <Button size="sm" variant="ghost" className="text-gray-600 hover:text-gray-800">
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderAchievementCard = (achievement) => (
    <Card key={achievement.id} className="bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-full bg-yellow-100">
              <Trophy className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <CardTitle className="text-lg text-yellow-800">{achievement.achievement_title}</CardTitle>
              <CardDescription className="text-yellow-600">
                {achievement.achievement_description}
              </CardDescription>
            </div>
          </div>
          <Badge className="bg-yellow-600 text-white">
            {achievement.rank_badge}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        <div className="flex items-center justify-between">
          <span className="text-sm text-yellow-600">
            Achieved on {new Date(achievement.generated_at).toLocaleDateString()}
          </span>
          <Button 
            size="sm" 
            variant="outline" 
            className="border-yellow-300 text-yellow-700 hover:bg-yellow-100"
            onClick={() => window.open(achievement.poster_url, '_blank')}
          >
            View Poster
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Helper functions for eBook display
  const getEbookDescription = (title) => {
    const descriptions = {
      "List Building for Real People: A Blue-Collar Starter Guide": "Master the fundamentals of building your first email list in 14 days using simple, mobile-first strategies designed for busy workers.",
      "Magnets That Work: 21 Lead Magnets You Can Build Tonight": "Create irresistible lead magnets that capture subscribers effectively. Includes 21 proven templates you can customize for any industry.",
      "From Followers to Owned Audience: Social → Email/SMS": "Convert your social media followers into owned contacts using platform-specific strategies and ethical automation techniques."
    };
    return descriptions[title] || "Advanced training content to grow your business skills.";
  };

  const getEbookTags = (title) => {
    const tags = {
      "List Building for Real People: A Blue-Collar Starter Guide": ["Beginner Friendly", "14-Day Plan", "Mobile First", "Blue-Collar Examples"],
      "Magnets That Work: 21 Lead Magnets You Can Build Tonight": ["Lead Magnets", "Templates", "Quick Setup", "Industry Specific"], 
      "From Followers to Owned Audience: Social → Email/SMS": ["Social Media", "Automation", "Platform Specific", "Conversion"]
    };
    return tags[title] || ["Training", "Education"];
  };

  const getEbookChapterCount = (title) => {
    const counts = {
      "List Building for Real People: A Blue-Collar Starter Guide": 10,
      "Magnets That Work: 21 Lead Magnets You Can Build Tonight": 10,
      "From Followers to Owned Audience: Social → Email/SMS": 10
    };
    return counts[title] || 8;
  };

  const getEbookFileName = (title) => {
    const fileNames = {
      "List Building for Real People: A Blue-Collar Starter Guide": "list-building-for-real-people-enhanced.pdf",
      "Magnets That Work: 21 Lead Magnets You Can Build Tonight": "magnets-that-work-enhanced.pdf",
      "From Followers to Owned Audience: Social → Email/SMS": "followers-to-owned-audience-enhanced.pdf"
    };
    return fileNames[title] || "training-content.pdf";
  };

  const handleStartEbook = (ebook) => {
    // For now, just download the PDF
    window.open(`/training/ebooks/${getEbookFileName(ebook.title)}`, '_blank');
    
    // In future, this would open interactive reading mode
    toast.success(`Opening ${ebook.title.split(':')[0]}...`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="h-8 w-8 text-blue-600" />
            Boot Camp Training Vault
          </h1>
          <p className="text-gray-600 mt-2">
            Access exclusive training materials and track your progress
          </p>
        </div>
        <Badge 
          className="px-4 py-2"
          style={{ 
            backgroundColor: accessLevelConfig[user?.membership_tier]?.color || '#6B7280',
            color: 'white'
          }}
        >
          {accessLevelConfig[user?.membership_tier]?.label || 'Free'} Member
        </Badge>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <BookOpen className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-gray-900">{trainingContent.length}</p>
                <p className="text-sm text-gray-600">Available Content</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {trainingContent.filter(c => c.user_progress === 100).length}
                </p>
                <p className="text-sm text-gray-600">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Trophy className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="text-2xl font-bold text-gray-900">{achievements.length}</p>
                <p className="text-sm text-gray-600">Achievements</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Zap className="h-5 w-5 text-purple-600" />
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {Math.round(trainingContent.reduce((acc, c) => acc + (c.user_progress || 0), 0) / Math.max(trainingContent.length, 1))}%
                </p>
                <p className="text-sm text-gray-600">Avg Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* eBook Progress Summary */}
      {ebookProgress && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <BookOpen className="h-5 w-5" />
              BBC Quick List Rookie Series Progress
            </CardTitle>
            <CardDescription>
              Complete all 3 eBooks to graduate from Boot Camp to Rookie Pro level
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Overall Progress</span>
                <span className="text-sm font-bold">{ebookProgress.overall_progress}%</span>
              </div>
              <Progress value={ebookProgress.overall_progress} className="h-3" />
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {ebookProgress.ebook_details.map((ebook, index) => (
                  <div key={ebook.id} className="bg-white p-4 rounded-lg border">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-sm">{ebook.title.replace(': A Blue-Collar Starter Guide', '').replace(': 21 Lead Magnets You Can Build Tonight', '').replace(': Social → Email/SMS', '')}</h4>
                      {ebook.completed && <CheckCircle className="h-4 w-4 text-green-600" />}
                    </div>
                    <Progress value={ebook.progress} className="h-2 mb-2" />
                    <span className="text-xs text-gray-600">{ebook.progress}% complete</span>
                  </div>
                ))}
              </div>
              
              {ebookProgress.next_recommendation && (
                <div className="bg-blue-100 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-1">Next Up:</h4>
                  <p className="text-sm text-blue-700">{ebookProgress.next_recommendation.title}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="ebooks">eBook Series</TabsTrigger>
          <TabsTrigger value="content">All Training</TabsTrigger>
          <TabsTrigger value="achievements">Achievements</TabsTrigger>
        </TabsList>

        <TabsContent value="ebooks" className="space-y-6">
          {ebookProgress ? (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Crown className="h-5 w-5 text-yellow-600" />
                    BBC Quick List Rookie Series
                  </CardTitle>
                  <CardDescription>
                    Master list building with our comprehensive 3-eBook series designed for real people with real jobs.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-6">
                    {ebookProgress.ebook_details.map((ebook, index) => (
                      <Card key={ebook.id} className={`transition-all duration-200 hover:shadow-lg ${
                        ebook.completed ? 'bg-green-50 border-green-200' : 
                        ebook.progress > 0 ? 'bg-blue-50 border-blue-200' : 
                        'hover:bg-gray-50'
                      }`}>
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h3 className="text-xl font-bold mb-2">{ebook.title}</h3>
                              <p className="text-gray-600 mb-3">{getEbookDescription(ebook.title)}</p>
                              
                              {ebook.progress > 0 && (
                                <div className="space-y-2 mb-4">
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-600">Progress</span>
                                    <span className="font-medium">{ebook.progress}%</span>
                                  </div>
                                  <Progress value={ebook.progress} className="h-2" />
                                </div>
                              )}
                              
                              <div className="flex flex-wrap gap-2 mb-4">
                                {getEbookTags(ebook.title).map((tag, tagIndex) => (
                                  <Badge key={tagIndex} variant="outline" className="text-xs">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            
                            <div className="flex flex-col items-end space-y-2 ml-4">
                              {ebook.completed ? (
                                <Badge className="bg-green-600 text-white">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Completed
                                </Badge>
                              ) : ebook.progress > 0 ? (
                                <Badge className="bg-blue-600 text-white">
                                  <PlayCircle className="h-3 w-3 mr-1" />
                                  In Progress
                                </Badge>
                              ) : (
                                <Badge variant="outline">
                                  <Star className="h-3 w-3 mr-1" />
                                  Start Reading
                                </Badge>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-500">
                              Book {index + 1} of 3 • {getEbookChapterCount(ebook.title)} chapters
                            </div>
                            
                            <div className="flex space-x-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => window.open(`/training/ebooks/${getEbookFileName(ebook.title)}`, '_blank')}
                              >
                                <Download className="h-4 w-4 mr-1" />
                                Download PDF
                              </Button>
                              <Button 
                                size="sm"
                                className={ebook.completed ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}
                                onClick={() => handleStartEbook(ebook)}
                              >
                                {ebook.completed ? (
                                  <>
                                    <Trophy className="h-4 w-4 mr-1" />
                                    Review
                                  </>
                                ) : ebook.progress > 0 ? (
                                  <>
                                    <PlayCircle className="h-4 w-4 mr-1" />
                                    Continue
                                  </>
                                ) : (
                                  <>
                                    <BookOpen className="h-4 w-4 mr-1" />
                                    Start Reading
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              {ebookProgress.completed_ebooks >= 3 && (
                <Card className="bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200">
                  <CardContent className="p-6 text-center">
                    <Trophy className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-yellow-800 mb-2">Congratulations, Rookie Graduate! 🎉</h3>
                    <p className="text-yellow-700 mb-4">
                      You've completed the entire BBC Quick List Rookie Series! You're now ready for Rookie Pro training.
                    </p>
                    <Button className="bg-yellow-600 hover:bg-yellow-700 text-white">
                      <Crown className="h-4 w-4 mr-2" />
                      Access Rookie Pro Training
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Loading eBook Series...</h3>
                <p className="text-gray-600">Please wait while we load your training progress.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="content" className="space-y-6">
          {/* Filters */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Filters</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Search</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search content..."
                      className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Category</label>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(category => (
                        <SelectItem key={category} value={category}>
                          {category === 'all' ? 'All Categories' : category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Content Type</label>
                  <Select value={selectedContentType} onValueChange={setSelectedContentType}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="ebook">eBooks</SelectItem>
                      <SelectItem value="podcast">Podcasts</SelectItem>
                      <SelectItem value="video_course">Video Courses</SelectItem>
                      <SelectItem value="ad_copy">Ad Copy</SelectItem>
                      <SelectItem value="playbook">Playbooks</SelectItem>
                      <SelectItem value="training_module">Training</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Content Grid */}
          {filteredContent.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredContent.map(renderContentCard)}
            </div>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Content Found</h3>
                <p className="text-gray-600">
                  {searchTerm || selectedCategory !== 'all' || selectedContentType !== 'all' 
                    ? 'Try adjusting your filters to see more content.'
                    : 'No training content available for your membership level.'}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="achievements" className="space-y-6">
          {achievements.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {achievements.map(renderAchievementCard)}
            </div>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <Trophy className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Achievements Yet</h3>
                <p className="text-gray-600">
                  Complete your profile, finish training content, and refer members to unlock achievements!
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BootCampTrainingVault;